import { ed25519 } from '@noble/curves/ed25519';
import { verifyChain, hashPredicateSource, normaliseForCanonical, canonicalJSON, lintDisclosureSignalPredicateName } from '@crawcus/spec';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

// src/dsse.ts
var CRAWCUS_PAYLOAD_TYPE_PREFIX = "application/vnd.crawcus.";
var CRAWCUS_PAYLOAD_TYPE_SUFFIX = "+jsonl";
var PAYLOAD_TYPE_AUDIT_BUNDLE = "application/vnd.crawcus.bundle+jsonl";
var KNOWN_CRAWCUS_SUBTYPES = /* @__PURE__ */ new Set([PAYLOAD_TYPE_AUDIT_BUNDLE]);
function isCrawcusPayloadType(payloadType) {
  return payloadType.startsWith(CRAWCUS_PAYLOAD_TYPE_PREFIX) && payloadType.endsWith(CRAWCUS_PAYLOAD_TYPE_SUFFIX);
}
function parseDsseEnvelope(bytes) {
  let text;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch (e) {
    return {
      kind: "parse-error",
      violationKind: "Envelope.shape.invalid",
      reason: `envelope bytes are not valid UTF-8: ${describe(e)}`
    };
  }
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return {
      kind: "parse-error",
      violationKind: "Envelope.shape.invalid",
      reason: `envelope is not valid JSON: ${describe(e)}`
    };
  }
  if (!isObject(parsed)) {
    return {
      kind: "parse-error",
      violationKind: "Envelope.shape.invalid",
      reason: "envelope is not a JSON object"
    };
  }
  const payloadType = parsed["payloadType"];
  if (typeof payloadType !== "string" || payloadType.length === 0) {
    return {
      kind: "parse-error",
      violationKind: "Envelope.shape.invalid",
      reason: `envelope.payloadType missing or not a non-empty string`
    };
  }
  const payload = parsed["payload"];
  if (typeof payload !== "string") {
    return {
      kind: "parse-error",
      violationKind: "Envelope.shape.invalid",
      reason: `envelope.payload missing or not a string`
    };
  }
  const signaturesRaw = parsed["signatures"];
  if (!Array.isArray(signaturesRaw) || signaturesRaw.length === 0) {
    return {
      kind: "parse-error",
      violationKind: "Envelope.shape.invalid",
      reason: `envelope.signatures must be a non-empty array`
    };
  }
  const signatures = [];
  for (let i = 0; i < signaturesRaw.length; i++) {
    const sig = signaturesRaw[i];
    if (!isObject(sig)) {
      return {
        kind: "parse-error",
        violationKind: "Envelope.shape.invalid",
        reason: `envelope.signatures[${String(i)}] is not an object`
      };
    }
    const keyid = sig["keyid"];
    const sigBytes = sig["sig"];
    if (typeof keyid !== "string" || keyid.length === 0) {
      return {
        kind: "parse-error",
        violationKind: "Envelope.shape.invalid",
        reason: `envelope.signatures[${String(i)}].keyid missing or empty`
      };
    }
    if (typeof sigBytes !== "string" || sigBytes.length === 0) {
      return {
        kind: "parse-error",
        violationKind: "Envelope.shape.invalid",
        reason: `envelope.signatures[${String(i)}].sig missing or empty`
      };
    }
    signatures.push({ keyid, sig: sigBytes });
  }
  return {
    kind: "ok",
    envelope: { payloadType, payload, signatures }
  };
}
function preAuthenticationEncoding(payloadType, payload) {
  const typeBytes = new TextEncoder().encode(payloadType);
  const header = `DSSEv1 ${String(typeBytes.length)} `;
  const sep = ` ${String(payload.length)} `;
  const headerBytes = new TextEncoder().encode(header);
  const sepBytes = new TextEncoder().encode(sep);
  const out = new Uint8Array(
    headerBytes.length + typeBytes.length + sepBytes.length + payload.length
  );
  let offset = 0;
  out.set(headerBytes, offset);
  offset += headerBytes.length;
  out.set(typeBytes, offset);
  offset += typeBytes.length;
  out.set(sepBytes, offset);
  offset += sepBytes.length;
  out.set(payload, offset);
  return out;
}
function decodeBase64Payload(base64) {
  return new Uint8Array(Buffer.from(base64, "base64"));
}
function decodeBase64Signature(base64) {
  return new Uint8Array(Buffer.from(base64, "base64"));
}
function decodeEd25519PublicKey(keyid) {
  if (/^[0-9a-fA-F]{64}$/.test(keyid)) {
    const bytes = new Uint8Array(32);
    for (let i = 0; i < 32; i++) {
      bytes[i] = parseInt(keyid.slice(i * 2, i * 2 + 2), 16);
    }
    return bytes;
  }
  if (/^[A-Za-z0-9+/]{43}=?$/.test(keyid)) {
    const raw = new Uint8Array(Buffer.from(keyid, "base64"));
    return raw.length === 32 ? raw : null;
  }
  return null;
}
function verifyDsseSignature(envelope, payloadBytes) {
  const firstSig = envelope.signatures[0];
  if (firstSig === void 0) {
    return {
      verdict: "fail",
      violationKind: "Envelope.signature.invalid",
      detail: "envelope.signatures is empty"
    };
  }
  const pubkey = decodeEd25519PublicKey(firstSig.keyid);
  if (pubkey === null) {
    return {
      verdict: "fail",
      violationKind: "Envelope.signature.invalid",
      detail: `signature keyid is not a valid 32-byte ed25519 public key (hex or base64): ${firstSig.keyid.slice(0, 16)}\u2026`
    };
  }
  let sigBytes;
  try {
    sigBytes = decodeBase64Signature(firstSig.sig);
  } catch (e) {
    return {
      verdict: "fail",
      violationKind: "Envelope.signature.invalid",
      detail: `signature is not valid base64: ${describe(e)}`
    };
  }
  if (sigBytes.length !== 64) {
    return {
      verdict: "fail",
      violationKind: "Envelope.signature.invalid",
      detail: `signature has wrong length (expected 64 bytes, got ${String(sigBytes.length)})`
    };
  }
  const pae = preAuthenticationEncoding(envelope.payloadType, payloadBytes);
  let ok;
  try {
    ok = ed25519.verify(sigBytes, pae, pubkey);
  } catch (e) {
    return {
      verdict: "fail",
      violationKind: "Envelope.signature.invalid",
      detail: `ed25519.verify threw: ${describe(e)}`
    };
  }
  if (!ok) {
    return {
      verdict: "fail",
      violationKind: "Envelope.signature.invalid",
      detail: "ed25519 signature did not verify against the embedded keyid"
    };
  }
  return { verdict: "pass", detail: "ed25519 signature verified against embedded keyid" };
}
function isObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function describe(e) {
  if (e instanceof Error) return e.message;
  return String(e);
}

// src/parse.ts
function parseSignedBundle(bytes) {
  const envelopeResult = parseDsseEnvelope(bytes);
  if (envelopeResult.kind === "parse-error") {
    return {
      kind: "fail",
      violationKind: envelopeResult.violationKind,
      detail: envelopeResult.reason
    };
  }
  const envelope = envelopeResult.envelope;
  let payloadBytes;
  try {
    payloadBytes = decodeBase64Payload(envelope.payload);
  } catch (e) {
    return {
      kind: "fail",
      violationKind: "Envelope.shape.invalid",
      detail: `envelope.payload is not valid base64: ${describe2(e)}`
    };
  }
  let payloadText;
  try {
    payloadText = new TextDecoder("utf-8", { fatal: true }).decode(payloadBytes);
  } catch (e) {
    return {
      kind: "fail",
      violationKind: "Envelope.shape.invalid",
      detail: `decoded payload is not valid UTF-8: ${describe2(e)}`
    };
  }
  let bundle;
  try {
    const parsed = JSON.parse(payloadText);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {
        kind: "fail",
        violationKind: "Envelope.shape.invalid",
        detail: "decoded payload is not a JSON object"
      };
    }
    bundle = parsed;
  } catch (e) {
    return {
      kind: "fail",
      violationKind: "Envelope.shape.invalid",
      detail: `decoded payload is not valid JSON: ${describe2(e)}`
    };
  }
  return {
    kind: "ok",
    parsed: { envelope, payloadBytes, bundle }
  };
}
function describe2(e) {
  if (e instanceof Error) return e.message;
  return String(e);
}
function sha256Hex(bytes) {
  return bytesToHex(sha256(bytes));
}
function verifyJcsHashEquivalence(bundle) {
  const chainProof = bundle.chainProof;
  if (typeof chainProof !== "object" || chainProof === null) {
    return {
      verdict: "pass",
      detail: "no chainProof present \u2014 JCS equivalence check skipped (legacy bundle)"
    };
  }
  const recordedRaw = chainProof["bundleSelfHash"];
  if (typeof recordedRaw !== "string" || recordedRaw.length === 0) {
    return {
      verdict: "pass",
      detail: "chainProof.bundleSelfHash absent \u2014 JCS equivalence check skipped (legacy bundle)"
    };
  }
  const canonicalTarget = stripSelfHash(bundle);
  let canonical;
  try {
    canonical = canonicalJSON(canonicalTarget);
  } catch (e) {
    return {
      verdict: "fail",
      detail: `payload not RFC-8785-canonicalisable: ${describe3(e)}`
    };
  }
  const recomputed = sha256Hex(new TextEncoder().encode(canonical));
  if (recomputed !== recordedRaw) {
    return {
      verdict: "fail",
      detail: `chainProof.bundleSelfHash mismatch: recorded ${recordedRaw}, recomputed ${recomputed}`,
      recordedHash: recordedRaw,
      recomputedHash: recomputed
    };
  }
  return {
    verdict: "pass",
    detail: "JCS canonicalisation hash equivalence verified",
    recordedHash: recordedRaw,
    recomputedHash: recomputed
  };
}
function stripSelfHash(bundle) {
  const out = { ...bundle };
  const chainProof = out["chainProof"];
  if (typeof chainProof === "object" && chainProof !== null) {
    const cp = { ...chainProof };
    delete cp["bundleSelfHash"];
    out["chainProof"] = cp;
  }
  return out;
}
function verifyEventChain(bundle) {
  if (bundle.events === void 0 || bundle.events === null) {
    return {
      verdict: "pass",
      detail: "no events in bundle \u2014 chain check vacuous"
    };
  }
  if (!Array.isArray(bundle.events)) {
    return {
      verdict: "fail",
      violationKind: "Chain.hash.broken",
      detail: "bundle.events is not an array"
    };
  }
  const events = bundle.events;
  const result = verifyChain(events);
  if (result.valid) {
    return {
      verdict: "pass",
      detail: `hash chain valid over ${String(events.length)} events`
    };
  }
  const reason = result.reason ?? "chain walk failed";
  const brokenAt = result.brokenAt ?? -1;
  return {
    verdict: "fail",
    violationKind: "Chain.hash.broken",
    detail: `hash chain broken at event index ${String(brokenAt)}: ${reason}`,
    brokenAt
  };
}
function describe3(e) {
  if (e instanceof Error) return e.message;
  return String(e);
}
function checkEmbeddedPredicates(bundle, options) {
  if (!options.requireEmbeddedPredicates) return [];
  const contractIds = extractReferencedContractIds(bundle);
  const sources = extractPredicateSourceMap(bundle);
  const missing = [];
  for (const id of contractIds) {
    if (!sources.has(id)) {
      missing.push({
        contractId: id,
        verdict: "fail",
        violationKind: "Contract.predicate.unembedded",
        detail: `Contract '${id}' is referenced in contractResults but has no predicate source text in bundle.predicateSources \u2014 bundle is not third-party verifiable per crawcus-format.md:446-449`
      });
    }
  }
  return missing;
}
function reevaluateContracts(bundle) {
  const results = extractContractResults(bundle);
  if (results === null) return [];
  const sources = extractPredicateSourceMap(bundle);
  const out = [];
  for (const r of results) {
    const id = extractContractId(r);
    if (id === null) {
      continue;
    }
    const checkpoint = normaliseCheckpoint(r.checkpoint);
    switch (r.result) {
      case "pass": {
        const recordedHash = r.predicateHash;
        const embedded = sources.get(id);
        if (typeof recordedHash === "string" && recordedHash.length > 0 && typeof embedded === "string") {
          const recomputed = hashPredicateSource(embedded);
          if (recomputed !== recordedHash) {
            out.push({
              contractId: id,
              verdict: "historical-unverifiable",
              violationKind: "Contract.predicate.retired",
              detail: `Contract '${id}': embedded predicate source hashes to ${recomputed} but recorded predicateHash is ${recordedHash} \u2014 predicate appears to have been edited or retired since the bundle was sealed`,
              ...checkpoint !== void 0 ? { checkpoint } : {}
            });
            break;
          }
        }
        out.push({
          contractId: id,
          verdict: "pass",
          detail: `Contract '${id}' historically evaluated to pass`,
          ...checkpoint !== void 0 ? { checkpoint } : {}
        });
        break;
      }
      case "fail": {
        const violationKind = checkpointToViolationKind(checkpoint);
        out.push({
          contractId: id,
          verdict: "fail",
          violationKind,
          detail: `Contract '${id}' historically failed at checkpoint '${checkpoint ?? "unknown"}' with severity '${r.severity ?? "unknown"}'`,
          ...checkpoint !== void 0 ? { checkpoint } : {}
        });
        break;
      }
      case "historical-unverifiable": {
        const seen = r.predicateHashSeen;
        out.push({
          contractId: id,
          verdict: "historical-unverifiable",
          violationKind: "Contract.predicate.retired",
          detail: `Contract '${id}' historical predicate (hash ${seen ?? "<unknown>"}) is unavailable for re-evaluation \u2014 recorded result was historical-unverifiable`,
          ...checkpoint !== void 0 ? { checkpoint } : {}
        });
        break;
      }
      default: {
        out.push({
          contractId: id,
          verdict: "historical-unverifiable",
          violationKind: "Contract.predicate.retired",
          detail: `Contract '${id}' has unknown historical result '${r.result}' \u2014 treated as historical-unverifiable`,
          ...checkpoint !== void 0 ? { checkpoint } : {}
        });
      }
    }
  }
  return out;
}
function lintSignalAsGate(bundle) {
  if (!hasDisclosureSignalEvent(bundle)) return [];
  const ids = extractReferencedContractIds(bundle);
  const out = [];
  for (const id of ids) {
    const lintResult = lintDisclosureSignalPredicateName(id);
    if (!lintResult.ok && lintResult.code === "FORBIDDEN_TOKEN") {
      out.push({
        contractId: id,
        verdict: "fail",
        violationKind: "Contract.predicate.signal-as-gate",
        detail: `Contract '${id}' uses forbidden token '${lintResult.token}' in its name \u2014 DisclosureSignal records SIGNAL (opportunity to perceive), never GATE (affirmative consent). See 02-product/crawcus-contracts.md \xA76.A.`
      });
    }
  }
  return out;
}
function extractContractResults(bundle) {
  const raw = bundle.contractResults;
  if (raw === void 0 || raw === null) return null;
  if (!Array.isArray(raw)) return null;
  return raw.filter((r) => {
    if (typeof r !== "object" || r === null) return false;
    const result = r["result"];
    return typeof result === "string";
  });
}
function extractContractId(r) {
  if (typeof r.contractId === "string" && r.contractId.length > 0) return r.contractId;
  if (typeof r.contract === "object" && r.contract !== null && typeof r.contract.id === "string" && r.contract.id.length > 0) {
    return r.contract.id;
  }
  return null;
}
function extractReferencedContractIds(bundle) {
  const results = extractContractResults(bundle);
  if (results === null) return [];
  const ids = /* @__PURE__ */ new Set();
  for (const r of results) {
    const id = extractContractId(r);
    if (id !== null) ids.add(id);
  }
  return Array.from(ids);
}
function extractPredicateSourceMap(bundle) {
  const raw = bundle.predicateSources;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return /* @__PURE__ */ new Map();
  const out = /* @__PURE__ */ new Map();
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === "string" && v.length > 0) out.set(k, v);
  }
  return out;
}
function hasDisclosureSignalEvent(bundle) {
  const events = bundle.events;
  if (!Array.isArray(events)) return false;
  for (const e of events) {
    if (typeof e === "object" && e !== null) {
      const kind = e["kind"];
      if (kind === "DisclosureSignal") return true;
    }
  }
  return false;
}
function normaliseCheckpoint(raw) {
  if (raw === "pre" || raw === "invariants" || raw === "post") return raw;
  return void 0;
}
function checkpointToViolationKind(cp) {
  switch (cp) {
    case "invariants":
      return "Contract.inv.violated";
    case "post":
      return "Contract.post.unmet";
    case "pre":
    default:
      return "Contract.pre.unmet";
  }
}

// src/version.ts
var VERIFIER_VERSION = "0.2.0";
var VERIFIER_PUBLIC_KEY_FINGERPRINT = "wave1-unsigned-no-verifier-output-signature";
var VERIFIER_BUILD_SHA = "unknown";

// src/verify.ts
var DEFAULT_OPTIONS = {
  requireEmbeddedPredicates: true,
  requireCrawcusPayloadType: true,
  reevaluatePredicates: true
};
function verifyBundle(input) {
  const opts = { ...DEFAULT_OPTIONS, ...input.options };
  const verifiedAt = input.options?.verifiedAt ?? (/* @__PURE__ */ new Date()).toISOString();
  const verifierIdentity = {
    version: VERIFIER_VERSION,
    publicKeyFingerprint: VERIFIER_PUBLIC_KEY_FINGERPRINT,
    buildSha: VERIFIER_BUILD_SHA
  };
  const parseResult = parseSignedBundle(input.bundle);
  if (parseResult.kind === "fail") {
    return failEarly({
      verifiedAt,
      verifierIdentity,
      bundleMetadata: emptyMetadata(),
      check: {
        id: "dsse.envelope.shape",
        label: "DSSE envelope shape",
        verdict: "fail",
        violationKind: parseResult.violationKind,
        detail: parseResult.detail
      }
    });
  }
  const parsed = parseResult.parsed;
  const metadata = extractMetadata(parsed);
  const checks = [];
  const shapeCheck = checkEnvelopeShape(parsed, opts.requireCrawcusPayloadType);
  checks.push(shapeCheck);
  if (shapeCheck.verdict === "fail") {
    return aggregateAndReturn(checks, metadata, verifiedAt, verifierIdentity);
  }
  const sigResult = verifyDsseSignature(parsed.envelope, parsed.payloadBytes);
  checks.push({
    id: "dsse.signature",
    label: "DSSE ed25519 signature",
    verdict: sigResult.verdict,
    ...sigResult.violationKind !== void 0 ? { violationKind: sigResult.violationKind } : {},
    detail: sigResult.detail,
    cite: [{ kind: "envelope-field", value: "signatures[0]" }]
  });
  if (sigResult.verdict === "fail") {
    return aggregateAndReturn(checks, metadata, verifiedAt, verifierIdentity);
  }
  const jcsResult = verifyJcsHashEquivalence(parsed.bundle);
  checks.push({
    id: "chain.jcs-hash-equivalence",
    label: "JCS canonicalisation hash equivalence (RFC 8785)",
    verdict: jcsResult.verdict,
    ...jcsResult.verdict === "fail" ? { violationKind: "Bundle.hash.mismatch" } : {},
    detail: jcsResult.detail
  });
  const chainResult = verifyEventChain(parsed.bundle);
  const chainCite = typeof chainResult.brokenAt === "number" && chainResult.brokenAt >= 0 ? [{ kind: "event-index", value: chainResult.brokenAt }] : void 0;
  checks.push({
    id: "chain.hash-chain",
    label: "Hash chain integrity",
    verdict: chainResult.verdict,
    ...chainResult.violationKind !== void 0 ? { violationKind: chainResult.violationKind } : {},
    detail: chainResult.detail,
    ...chainCite !== void 0 ? { cite: chainCite } : {}
  });
  const embeddedResults = checkEmbeddedPredicates(parsed.bundle, {
    requireEmbeddedPredicates: opts.requireEmbeddedPredicates
  });
  for (const r of embeddedResults) {
    checks.push({
      id: `contract.predicate.unembedded.${r.contractId}`,
      label: `Embedded predicate source required (${r.contractId})`,
      verdict: r.verdict,
      ...r.violationKind !== void 0 ? { violationKind: r.violationKind } : {},
      detail: r.detail,
      cite: [{ kind: "contract-id", value: r.contractId }]
    });
  }
  if (opts.reevaluatePredicates) {
    const contractResults = reevaluateContracts(parsed.bundle);
    for (const r of contractResults) {
      checks.push({
        id: `contract.${r.checkpoint ?? "unknown"}.${r.contractId}`,
        label: `Contract re-evaluation (${r.checkpoint ?? "unknown"}: ${r.contractId})`,
        verdict: r.verdict,
        ...r.violationKind !== void 0 ? { violationKind: r.violationKind } : {},
        detail: r.detail,
        cite: [{ kind: "contract-id", value: r.contractId }]
      });
    }
  }
  const signalLintResults = lintSignalAsGate(parsed.bundle);
  for (const r of signalLintResults) {
    checks.push({
      id: `contract.signal-as-gate.${r.contractId}`,
      label: `DisclosureSignal SIGNAL-not-gate lint (${r.contractId})`,
      verdict: r.verdict,
      ...r.violationKind !== void 0 ? { violationKind: r.violationKind } : {},
      detail: r.detail,
      cite: [{ kind: "contract-id", value: r.contractId }]
    });
  }
  return aggregateAndReturn(checks, metadata, verifiedAt, verifierIdentity);
}
function checkEnvelopeShape(parsed, requireCrawcusPayloadType) {
  if (!requireCrawcusPayloadType) {
    return {
      id: "dsse.envelope.shape",
      label: "DSSE envelope shape",
      verdict: "pass",
      detail: `envelope parsed; payloadType '${parsed.envelope.payloadType}' (family check skipped per options)`,
      cite: [{ kind: "envelope-field", value: "payloadType" }]
    };
  }
  if (!isCrawcusPayloadType(parsed.envelope.payloadType)) {
    return {
      id: "dsse.envelope.shape",
      label: "DSSE envelope shape",
      verdict: "fail",
      violationKind: "Envelope.shape.invalid",
      detail: `envelope.payloadType '${parsed.envelope.payloadType}' is not in the application/vnd.crawcus.*+jsonl family per crawcus-format.md:667-679`,
      cite: [{ kind: "envelope-field", value: "payloadType" }]
    };
  }
  const known = KNOWN_CRAWCUS_SUBTYPES.has(parsed.envelope.payloadType);
  return {
    id: "dsse.envelope.shape",
    label: "DSSE envelope shape",
    verdict: "pass",
    detail: known ? `envelope parsed; payloadType '${parsed.envelope.payloadType}' known to Wave-1 verifier` : `envelope parsed; payloadType '${parsed.envelope.payloadType}' is in CRAWCUS family but unknown to Wave-1 (forward-compat fall-through per crawcus-format.md:677)`,
    cite: [{ kind: "envelope-field", value: "payloadType" }]
  };
}
function aggregateAndReturn(checks, bundleMetadata, verifiedAt, verifierIdentity) {
  const verdict = aggregateVerdict(checks);
  return { verdict, checks, bundleMetadata, verifiedAt, verifierIdentity };
}
function aggregateVerdict(checks) {
  let sawHistoricalUnverifiable = false;
  for (const c of checks) {
    if (c.verdict === "fail") return "fail";
    if (c.verdict === "historical-unverifiable") sawHistoricalUnverifiable = true;
  }
  return sawHistoricalUnverifiable ? "historical-unverifiable" : "pass";
}
function failEarly(args) {
  return {
    verdict: "fail",
    checks: [args.check],
    bundleMetadata: args.bundleMetadata,
    verifiedAt: args.verifiedAt,
    verifierIdentity: args.verifierIdentity
  };
}
function emptyMetadata() {
  return {
    bundleId: "",
    schemaVersion: "",
    payloadType: "",
    signerKeyId: "",
    eventCount: 0,
    contractCount: 0,
    earliestEventTs: "",
    latestEventTs: ""
  };
}
function extractMetadata(parsed) {
  const bundle = parsed.bundle;
  const events = Array.isArray(bundle.events) ? bundle.events : [];
  const contractResults = Array.isArray(bundle.contractResults) ? bundle.contractResults : [];
  const firstSig = parsed.envelope.signatures[0];
  let earliestTs = "";
  let latestTs = "";
  for (const e of events) {
    if (typeof e === "object" && e !== null) {
      const ts = e["timestamp"];
      if (typeof ts === "string") {
        if (earliestTs === "" || ts < earliestTs) earliestTs = ts;
        if (ts > latestTs) latestTs = ts;
      }
    }
  }
  const intent = bundle.intent;
  const intentId = typeof intent === "object" && intent !== null ? String(intent["id"] ?? "") : "";
  const bundleVersion = typeof bundle.bundleVersion === "string" ? bundle.bundleVersion : "";
  return {
    bundleId: intentId,
    schemaVersion: bundleVersion,
    payloadType: parsed.envelope.payloadType,
    signerKeyId: firstSig?.keyid ?? "",
    eventCount: events.length,
    contractCount: contractResults.length,
    earliestEventTs: earliestTs,
    latestEventTs: latestTs
  };
}
var PAYLOAD_TYPE_VERIFY_RESULT = "application/vnd.crawcus.verify-result+jsonl";
function countersignResult(input) {
  const privateKey = decodeAuditorPrivateKey(input.signerKey);
  const publicKey = ed25519.getPublicKey(privateKey);
  const keyid = input.signerKeyId ?? bytesToHex2(publicKey);
  const signedAt = input.signedAt ?? (/* @__PURE__ */ new Date()).toISOString();
  const canonicalisedTarget = normaliseForCanonical({ ...input.result, signedAt });
  const canonicalPayload = canonicalJSON(canonicalisedTarget);
  const payloadBytes = new TextEncoder().encode(canonicalPayload);
  const pae = preAuthenticationEncoding(PAYLOAD_TYPE_VERIFY_RESULT, payloadBytes);
  const sigBytes = ed25519.sign(pae, privateKey);
  const envelope = {
    payloadType: PAYLOAD_TYPE_VERIFY_RESULT,
    payload: bytesToBase64(payloadBytes),
    signatures: [
      {
        keyid,
        sig: bytesToBase64(sigBytes)
      }
    ]
  };
  const envelopeBytes = new TextEncoder().encode(JSON.stringify(envelope));
  return {
    envelope: envelopeBytes,
    envelopeMetadata: {
      payloadType: PAYLOAD_TYPE_VERIFY_RESULT,
      signerKeyId: keyid,
      signedAt
    }
  };
}
function decodeAuditorPrivateKey(blob) {
  if (blob instanceof Uint8Array) {
    if (blob.length !== 32) {
      throw new Error(`signer key Uint8Array must be 32 bytes (got ${String(blob.length)})`);
    }
    return blob;
  }
  const trimmed = blob.trim();
  if (/^[0-9a-fA-F]{64}$/.test(trimmed)) {
    const out = new Uint8Array(32);
    for (let i = 0; i < 32; i++) {
      out[i] = parseInt(trimmed.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
  }
  if (trimmed.startsWith("-----BEGIN ")) {
    return decodePkcs8EdPrivateKey(trimmed);
  }
  throw new Error("signer key must be a 32-byte Uint8Array, a 64-char hex string, or a PKCS#8 PEM");
}
function decodePkcs8EdPrivateKey(pem) {
  const begin = "-----BEGIN PRIVATE KEY-----";
  const end = "-----END PRIVATE KEY-----";
  const beginIdx = pem.indexOf(begin);
  const endIdx = pem.indexOf(end);
  if (beginIdx < 0 || endIdx < 0) {
    throw new Error("PEM blob missing PRIVATE KEY armour");
  }
  const b64 = pem.slice(beginIdx + begin.length, endIdx).replace(/[\r\n\s]/g, "");
  let der;
  try {
    der = new Uint8Array(Buffer.from(b64, "base64"));
  } catch (e) {
    throw new Error(`PEM base64 decode failed: ${describe4(e)}`);
  }
  if (der.length !== 48) {
    throw new Error(`expected 48-byte canonical ed25519 PKCS#8 DER (got ${String(der.length)})`);
  }
  if (der[0] !== 48 || der[1] !== 46) {
    throw new Error("PKCS#8 outer SEQUENCE header mismatch");
  }
  if (der[2] !== 2 || der[3] !== 1 || der[4] !== 0) {
    throw new Error("PKCS#8 version mismatch");
  }
  if (der[5] !== 48 || der[6] !== 5 || der[7] !== 6 || der[8] !== 3 || der[9] !== 43 || der[10] !== 101 || der[11] !== 112) {
    throw new Error("PKCS#8 algorithm OID is not id-Ed25519");
  }
  if (der[12] !== 4 || der[13] !== 34 || der[14] !== 4 || der[15] !== 32) {
    throw new Error("PKCS#8 inner OCTET STRING wrapper mismatch");
  }
  return der.slice(16, 48);
}
function bytesToBase64(bytes) {
  return Buffer.from(bytes).toString("base64");
}
function bytesToHex2(bytes) {
  let out = "";
  for (const b of bytes) {
    out += b.toString(16).padStart(2, "0");
  }
  return out;
}
function describe4(e) {
  if (e instanceof Error) return e.message;
  return String(e);
}

// src/format/pdf.ts
var PAGE_WIDTH = 612;
var PAGE_HEIGHT = 792;
var MARGIN_LEFT = 56;
var MARGIN_TOP = 56;
var MARGIN_BOTTOM = 56;
var LINE_HEIGHT = 14;
var HEADING_SIZE = 18;
var SUBHEAD_SIZE = 12;
var BODY_SIZE = 10;
var FOOTER_SIZE = 8;
var SIGNATURE_LINE_LABEL = "Reviewed by:";
var SIGNATURE_DATE_LABEL = "Date:";
function renderPdf(result) {
  const builder = new PdfBuilder();
  layoutReport(builder, result);
  return builder.finish(result.verifiedAt);
}
function layoutReport(builder, result) {
  builder.text(verdictHeadline(result), HEADING_SIZE, true);
  builder.gap(LINE_HEIGHT);
  builder.text("Bundle metadata", SUBHEAD_SIZE, true);
  builder.gap(LINE_HEIGHT / 2);
  const m = result.bundleMetadata;
  builder.text(`Bundle id:       ${nonEmpty(m.bundleId)}`, BODY_SIZE);
  builder.text(`Schema version:  ${nonEmpty(m.schemaVersion)}`, BODY_SIZE);
  builder.text(`Payload type:    ${nonEmpty(m.payloadType)}`, BODY_SIZE);
  builder.text(`Signer key id:   ${nonEmpty(m.signerKeyId)}`, BODY_SIZE);
  builder.text(
    `Events: ${String(m.eventCount)}    Contracts: ${String(m.contractCount)}`,
    BODY_SIZE
  );
  builder.text(
    `Earliest event: ${nonEmpty(m.earliestEventTs)}    Latest: ${nonEmpty(m.latestEventTs)}`,
    BODY_SIZE
  );
  builder.gap(LINE_HEIGHT);
  builder.text("Checks", SUBHEAD_SIZE, true);
  builder.gap(LINE_HEIGHT / 2);
  for (const c of result.checks) {
    layoutCheckRow(builder, c);
  }
  builder.gap(LINE_HEIGHT);
  builder.text("Verifier identity", SUBHEAD_SIZE, true);
  builder.gap(LINE_HEIGHT / 2);
  builder.text(`crawcus-verify ${result.verifierIdentity.version}`, BODY_SIZE);
  builder.text(`Build SHA:           ${result.verifierIdentity.buildSha}`, BODY_SIZE);
  builder.text(`Public key fp:       ${result.verifierIdentity.publicKeyFingerprint}`, BODY_SIZE);
  builder.text(`Verified at:         ${result.verifiedAt}`, BODY_SIZE);
  builder.gap(LINE_HEIGHT * 2);
  builder.text("Auditor sign-off", SUBHEAD_SIZE, true);
  builder.gap(LINE_HEIGHT);
  builder.text(
    `${SIGNATURE_LINE_LABEL} ____________________________________________________`,
    BODY_SIZE
  );
  builder.gap(LINE_HEIGHT * 2);
  builder.text(
    `${SIGNATURE_DATE_LABEL} ____________________________________________________`,
    BODY_SIZE
  );
  builder.footer(
    `crawcus-verify ${result.verifierIdentity.version} \u2014 generated ${result.verifiedAt}`,
    FOOTER_SIZE
  );
}
function layoutCheckRow(builder, c) {
  const marker = checkMarker(c.verdict);
  const kind = c.violationKind !== void 0 ? ` [${c.violationKind}]` : "";
  builder.text(`${marker} ${c.id}${kind}`, BODY_SIZE, false);
  const wrapped = wrapText(c.detail, 86);
  for (const line of wrapped) {
    builder.text(`    ${line}`, BODY_SIZE, false);
  }
}
function verdictHeadline(result) {
  switch (result.verdict) {
    case "pass":
      return "CRAWCUS VERIFY \u2014 PASS";
    case "fail":
      return "CRAWCUS VERIFY \u2014 FAIL";
    case "historical-unverifiable":
      return "CRAWCUS VERIFY \u2014 HISTORICAL-UNVERIFIABLE";
    default: {
      result.verdict;
      return "CRAWCUS VERIFY";
    }
  }
}
function checkMarker(v) {
  switch (v) {
    case "pass":
      return "[PASS]";
    case "fail":
      return "[FAIL]";
    case "historical-unverifiable":
      return "[HIST]";
    case "skipped":
      return "[SKIP]";
    default: {
      return "[?]";
    }
  }
}
function nonEmpty(s) {
  return s === "" ? "<unknown>" : s;
}
function wrapText(text, width) {
  const out = [];
  const paragraphs = text.split("\n");
  for (const p of paragraphs) {
    if (p.length <= width) {
      out.push(p);
      continue;
    }
    const words = p.split(" ");
    let line = "";
    for (const w of words) {
      if (line.length === 0) {
        line = w;
        continue;
      }
      if (line.length + 1 + w.length > width) {
        out.push(line);
        line = w;
      } else {
        line = `${line} ${w}`;
      }
    }
    if (line.length > 0) out.push(line);
  }
  return out;
}
var PdfBuilder = class {
  pages = [""];
  cursor = PAGE_HEIGHT - MARGIN_TOP;
  footerLine = null;
  footerSize = FOOTER_SIZE;
  /** Add a text line at the current cursor + advance the cursor. */
  text(content, size, bold = false) {
    const lineHeight = Math.max(LINE_HEIGHT, size + 4);
    if (this.cursor - lineHeight < MARGIN_BOTTOM + LINE_HEIGHT) {
      this.newPage();
    }
    const safe = sanitizeForPdf(content);
    const fontRef = bold ? "F2" : "F1";
    const op = `BT
/${fontRef} ${String(size)} Tf
${String(MARGIN_LEFT)} ${String(this.cursor - size)} Td
(${safe}) Tj
ET
`;
    this.pages[this.pages.length - 1] = `${this.pages[this.pages.length - 1] ?? ""}${op}`;
    this.cursor -= lineHeight;
  }
  /** Advance the cursor by `gap` user units without drawing. */
  gap(gap) {
    this.cursor -= gap;
  }
  /** Set a footer line to render at the bottom of the last page. */
  footer(content, size) {
    this.footerLine = content;
    this.footerSize = size;
  }
  /** Force a new page. */
  newPage() {
    this.pages.push("");
    this.cursor = PAGE_HEIGHT - MARGIN_TOP;
  }
  /** Emit the full PDF bytes. */
  finish(verifiedAt) {
    if (this.footerLine !== null) {
      const safe = sanitizeForPdf(this.footerLine);
      const op = `BT
/F1 ${String(this.footerSize)} Tf
${String(MARGIN_LEFT)} ${String(MARGIN_BOTTOM - this.footerSize - 4)} Td
(${safe}) Tj
ET
`;
      this.pages[this.pages.length - 1] = `${this.pages[this.pages.length - 1] ?? ""}${op}`;
    }
    const objects = [];
    objects.push("<< /Type /Catalog /Pages 2 0 R >>");
    const pageCount = this.pages.length;
    const pageRefs = [];
    for (let i = 0; i < pageCount; i++) {
      pageRefs.push(`${String(5 + i * 2)} 0 R`);
    }
    objects.push(
      `<< /Type /Pages /Count ${String(pageCount)} /Kids [ ${pageRefs.join(" ")} ] /MediaBox [0 0 ${String(PAGE_WIDTH)} ${String(PAGE_HEIGHT)}] >>`
    );
    objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
    objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>");
    for (let i = 0; i < pageCount; i++) {
      const contentRef = 5 + i * 2 + 1;
      objects.push(
        `<< /Type /Page /Parent 2 0 R /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${String(contentRef)} 0 R >>`
      );
      const stream = this.pages[i] ?? "";
      const length = stream.length;
      objects.push(`<< /Length ${String(length)} >>
stream
${stream}endstream`);
    }
    let body = "%PDF-1.4\n%\xE2\xE3\xCF\xD3\n";
    const offsets = [];
    for (let i = 0; i < objects.length; i++) {
      offsets.push(byteLength(body));
      body += `${String(i + 1)} 0 obj
${objects[i] ?? ""}
endobj
`;
    }
    const xrefOffset = byteLength(body);
    let xref = `xref
0 ${String(objects.length + 1)}
`;
    xref += "0000000000 65535 f \n";
    for (const o of offsets) {
      xref += `${o.toString(10).padStart(10, "0")} 00000 n 
`;
    }
    const idHex = stableIdHex(verifiedAt);
    const trailer = `trailer
<< /Size ${String(objects.length + 1)} /Root 1 0 R /ID [ <${idHex}> <${idHex}> ] /Producer (crawcus-verify) /CreationDate (D:${pdfDate(verifiedAt)}) >>
startxref
${String(xrefOffset)}
%%EOF
`;
    const tail = `${xref}${trailer}`;
    const out = new TextEncoder().encode(`${body}${tail}`);
    return out;
  }
};
function sanitizeForPdf(content) {
  let out = "";
  for (const ch of content) {
    const code = ch.charCodeAt(0);
    if (code === 40) {
      out += "\\(";
      continue;
    }
    if (code === 41) {
      out += "\\)";
      continue;
    }
    if (code === 92) {
      out += "\\\\";
      continue;
    }
    if (code >= 32 && code <= 126) {
      out += ch;
      continue;
    }
    out += "?";
  }
  return out;
}
function byteLength(s) {
  return new TextEncoder().encode(s).length;
}
function pdfDate(isoTs) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(isoTs);
  if (m === null) {
    return "00000101000000Z";
  }
  return `${m[1] ?? "0000"}${m[2] ?? "01"}${m[3] ?? "01"}${m[4] ?? "00"}${m[5] ?? "00"}${m[6] ?? "00"}Z`;
}
function stableIdHex(isoTs) {
  let hash = 2654435769;
  for (let i = 0; i < isoTs.length; i++) {
    hash = (hash ^ isoTs.charCodeAt(i)) >>> 0;
    hash = Math.imul(hash, 2246822507) >>> 0;
    hash = (hash ^ hash >>> 13) >>> 0;
  }
  let out = "";
  let state = hash;
  for (let i = 0; i < 16; i++) {
    state = (Math.imul(state, 668265261) ^ state >>> 15) >>> 0;
    out += (state & 255).toString(16).padStart(2, "0");
  }
  return out;
}

export { CRAWCUS_PAYLOAD_TYPE_PREFIX, CRAWCUS_PAYLOAD_TYPE_SUFFIX, KNOWN_CRAWCUS_SUBTYPES, PAYLOAD_TYPE_AUDIT_BUNDLE, PAYLOAD_TYPE_VERIFY_RESULT, VERIFIER_VERSION, countersignResult, parseSignedBundle, reevaluateContracts, renderPdf, verifyBundle, verifyDsseSignature as verifyDsseEnvelope, verifyEventChain as verifyHashChain };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map